import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('nospin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: (email, password) => 
    axios.post(`${API_URL}/api/auth/login`, { email, password }),
  
  register: (email, password, name) => 
    axios.post(`${API_URL}/api/auth/register`, { email, password, name }),
  
  getMe: () => 
    axios.get(`${API_URL}/api/auth/me`, { headers: getAuthHeaders() }),

  // Forgot Password
  forgotPassword: (email) =>
    axios.post(`${API_URL}/api/auth/forgot-password`, { email }),
  
  resetPassword: (token, newPassword) =>
    axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword }),

  // Google OAuth
  googleAuth: (sessionId) =>
    axios.post(`${API_URL}/api/auth/google`, { session_id: sessionId }),

  // Terms of Use
  getTerms: () =>
    axios.get(`${API_URL}/api/terms`),
  
  acceptTerms: (accepted) =>
    axios.post(`${API_URL}/api/auth/accept-terms`, { accepted }, { headers: getAuthHeaders() }),

  // Profile
  completeOnboarding: (data) =>
    axios.post(`${API_URL}/api/profile/onboarding`, data, { headers: getAuthHeaders() }),
  
  updateProfile: (data) =>
    axios.put(`${API_URL}/api/profile/update`, data, { headers: getAuthHeaders() }),

  // Search
  search: (query) =>
    axios.post(`${API_URL}/api/search`, { query }, { headers: getAuthHeaders() }),

  // Usage
  checkUsage: () =>
    axios.get(`${API_URL}/api/usage/check`, { headers: getAuthHeaders() }),
  
  bypassDonationPrompt: () =>
    axios.post(`${API_URL}/api/usage/bypass`, {}, { headers: getAuthHeaders() }),

  // History
  getQueries: (starredOnly = false) =>
    axios.get(`${API_URL}/api/queries`, { 
      headers: getAuthHeaders(),
      params: { starred_only: starredOnly }
    }),
  
  toggleStar: (queryId) =>
    axios.post(`${API_URL}/api/queries/${queryId}/star`, {}, { headers: getAuthHeaders() }),
  
  deleteQuery: (queryId) =>
    axios.delete(`${API_URL}/api/queries/${queryId}`, { headers: getAuthHeaders() }),
  
  exportQuery: (queryId) =>
    axios.get(`${API_URL}/api/queries/${queryId}/export`, { headers: getAuthHeaders() }),

  // Payments / Donations
  getDonationTiers: () =>
    axios.get(`${API_URL}/api/donations/tiers`, { headers: getAuthHeaders() }),
  
  createDonationCheckout: (tier, originUrl, ambassadorName = null) =>
    axios.post(`${API_URL}/api/donations/checkout`, { tier, origin_url: originUrl, ambassador_name: ambassadorName }, { headers: getAuthHeaders() }),
  
  getDonationStatus: (sessionId) =>
    axios.get(`${API_URL}/api/donations/status/${sessionId}`, { headers: getAuthHeaders() }),
  
  getAmbassadors: () =>
    axios.get(`${API_URL}/api/ambassadors`),

  // Follow-up queries
  searchFollowup: (query, parentQueryId) =>
    axios.post(`${API_URL}/api/search/followup`, { query, parent_query_id: parentQueryId }, { headers: getAuthHeaders() }),

  // Sharing
  shareResults: (queryId, shareType, recipient, message = null) =>
    axios.post(`${API_URL}/api/share`, { query_id: queryId, share_type: shareType, recipient, message }, { headers: getAuthHeaders() }),

  // Feedback
  submitFeedback: (data) =>
    axios.post(`${API_URL}/api/feedback`, data, { headers: getAuthHeaders() }),
  
  submitQuickFeedback: (appRating, accuracyRating, content = null) =>
    axios.post(`${API_URL}/api/feedback`, {
      feedback_type: 'quick_feedback',
      app_rating: appRating,
      accuracy_rating: accuracyRating,
      content: content
    }, { headers: getAuthHeaders() }),
  
  getFeedbackStats: () =>
    axios.get(`${API_URL}/api/feedback/stats`),

  // CME Credits
  getCMESummary: () =>
    axios.get(`${API_URL}/api/cme/summary`, { headers: getAuthHeaders() }),
  
  exportCMECredits: (startDate = null, endDate = null) =>
    axios.post(`${API_URL}/api/cme/export`, { start_date: startDate, end_date: endDate }, { headers: getAuthHeaders() }),

  // Analytics
  trackEvent: (eventType, eventData = null) =>
    axios.post(`${API_URL}/api/analytics/event`, { event_type: eventType, event_data: eventData }, { headers: getAuthHeaders() }),

  // Admin Analytics
  getAdminOverview: () =>
    axios.get(`${API_URL}/api/admin/analytics/overview`, { headers: getAuthHeaders() }),
  
  getAdminUserAnalytics: (days = 30) =>
    axios.get(`${API_URL}/api/admin/analytics/users`, { headers: getAuthHeaders(), params: { days } }),
  
  getAdminSearchAnalytics: (days = 30) =>
    axios.get(`${API_URL}/api/admin/analytics/searches`, { headers: getAuthHeaders(), params: { days } }),
  
  getAdminTopicAnalytics: () =>
    axios.get(`${API_URL}/api/admin/analytics/topics`, { headers: getAuthHeaders() }),
  
  getAdminEngagementAnalytics: (days = 30) =>
    axios.get(`${API_URL}/api/admin/analytics/engagement`, { headers: getAuthHeaders(), params: { days } }),
  
  getAdminRetentionAnalytics: () =>
    axios.get(`${API_URL}/api/admin/analytics/retention`, { headers: getAuthHeaders() }),

  getAdminTimeSeries: (period = 'all') =>
    axios.get(`${API_URL}/api/admin/analytics/time-series`, { headers: getAuthHeaders(), params: { period } }),

  // Admin Email Alerts
  sendTestEmail: () =>
    axios.post(`${API_URL}/api/admin/test-email`, {}, { headers: getAuthHeaders() }),
  
  sendDailySummary: () =>
    axios.post(`${API_URL}/api/admin/send-daily-summary`, {}, { headers: getAuthHeaders() }),

  // Admin User Management
  getAdminUsers: (revealEmails = false) =>
    axios.get(`${API_URL}/api/admin/users`, { headers: getAuthHeaders(), params: { reveal_emails: revealEmails } }),
  
  searchUsers: (query, revealEmails = false) =>
    axios.get(`${API_URL}/api/admin/users/search`, { headers: getAuthHeaders(), params: { query, reveal_emails: revealEmails } }),
  
  makeAdmin: (email) =>
    axios.post(`${API_URL}/api/admin/make-admin/${encodeURIComponent(email)}`, {}, { headers: getAuthHeaders() }),
  
  revokeAdmin: (email) =>
    axios.post(`${API_URL}/api/admin/revoke-admin/${encodeURIComponent(email)}`, {}, { headers: getAuthHeaders() }),

  // Admin Feedback
  getAdminFeedback: (feedbackType = null, limit = 100, skip = 0, revealEmails = false) =>
    axios.get(`${API_URL}/api/admin/feedback`, { 
      headers: getAuthHeaders(), 
      params: { feedback_type: feedbackType, limit, skip, reveal_emails: revealEmails } 
    }),

  // Admin NLP Query
  adminNlpQuery: (query) =>
    axios.post(`${API_URL}/api/admin/nlp-query`, { query }, { headers: getAuthHeaders() }),

  // Admin Specialty Normalization
  normalizeSpecialties: () =>
    axios.post(`${API_URL}/api/admin/normalize-specialties`, {}, { headers: getAuthHeaders() }),

  // About
  getAboutInfo: () =>
    axios.get(`${API_URL}/api/about`),
};

export default api;
